import api from '../api/api';

const FORMS_FILES_BASE_URL =
  'https://hub2.ntactops.com/ntact/files/forms_files';

/**
 * Returns true when `img` is a server-uploaded reference object
 * ({ file_name, file_path }) rather than a base64 string or URL.
 */
export const isUploadedRef = (img) =>
  img != null &&
  typeof img === 'object' &&
  !Array.isArray(img) &&
  (typeof img.file_name === 'string' || typeof img.file_path === 'string');

/**
 * Produce a displayable URL/data-URI from any image shape stored in a draft.
 *
 * Shape in form_data         → Display
 * { file_name, file_path }   → BASE_URL + "/" + file_name
 * "data:image/...;base64,…"  → return as-is
 * "https://…"                → return as-is
 */
export const toDisplayUrl = (img) => {
  if (!img) return '';

  if (isUploadedRef(img)) {
    return img.file_name
      ? `${FORMS_FILES_BASE_URL}/${img.file_name}`
      : '';
  }

  if (typeof img === 'string') return img;

  return '';
};

/**
 * Prepare an image value for the createFastForm / create-form submit payload.
 *
 * - Already-uploaded ref → pass through { file_name, file_path }.
 * - base64 data URI      → upload now via /v1/forms/upload-files, return the ref.
 * - Public URL string    → derive file_name from the last path segment.
 */
export const toSubmitRef = async (img) => {
  if (isUploadedRef(img)) {
    return { file_name: img.file_name, file_path: img.file_path };
  }

  if (typeof img === 'string' && img.startsWith('data:')) {
    const refs = await uploadBase64Images([img]);
    return refs[0] || null;
  }

  if (typeof img === 'string' && img.startsWith('http')) {
    const fileName = img.split('/').pop().split('?')[0];
    return { file_name: fileName, file_path: '' };
  }

  return null;
};

/**
 * Batch-convert an array of image values (mixed shapes) into submit-ready refs.
 * Collects all base64 items, uploads them in a single API call, then maps back.
 */
export const toSubmitRefs = async (images) => {
  if (!Array.isArray(images) || images.length === 0) return [];

  const results = new Array(images.length).fill(null);
  const base64Indices = [];
  const base64Items = [];

  images.forEach((img, i) => {
    if (isUploadedRef(img)) {
      results[i] = { file_name: img.file_name, file_path: img.file_path };
    } else if (typeof img === 'string' && img.startsWith('data:')) {
      base64Indices.push(i);
      base64Items.push(img);
    } else if (typeof img === 'string' && img.startsWith('http')) {
      const fileName = img.split('/').pop().split('?')[0];
      results[i] = { file_name: fileName, file_path: '' };
    }
  });

  if (base64Items.length > 0) {
    const refs = await uploadBase64Images(base64Items);
    base64Indices.forEach((origIdx, j) => {
      results[origIdx] = refs[j] || null;
    });
  }

  return results.filter(Boolean);
};

/**
 * Upload a File object immediately and return { file_name, file_path }.
 */
export const uploadFileNow = async (file) => {
  if (!(file instanceof File)) return null;
  const fd = new FormData();
  fd.append('files', file);
  try {
    const resp = await api.post('/v1/forms/upload-files', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const files = Array.isArray(resp?.files) ? resp.files : [];
    return files[0] || null;
  } catch (err) {
    console.error('uploadFileNow failed:', err);
    return null;
  }
};

/**
 * Upload multiple File objects in one call and return array of refs.
 */
export const uploadFilesNow = async (fileList) => {
  if (!fileList || fileList.length === 0) return [];
  const fd = new FormData();
  fileList.forEach((f) => fd.append('files', f));
  try {
    const resp = await api.post('/v1/forms/upload-files', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return Array.isArray(resp?.files) ? resp.files : [];
  } catch (err) {
    console.error('uploadFilesNow failed:', err);
    return [];
  }
};

// Internal: convert base64 data URIs to File objects, upload, return refs.
const uploadBase64Images = async (dataUris) => {
  const files = dataUris.map((uri, i) => dataUriToFile(uri, `draft_img_${Date.now()}_${i}.jpg`));
  return uploadFilesNow(files);
};

const dataUriToFile = (dataUri, filename) => {
  const [header, data] = dataUri.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(data);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    arr[i] = binary.charCodeAt(i);
  }
  return new File([arr], filename, { type: mime });
};

export { FORMS_FILES_BASE_URL };
